import React from "react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";

const DATA_TABLE = [
  { cat: "Account Information", data: "Name, email address, company name, password (hashed)", purpose: "Account creation and authentication" },
  { cat: "Usage Data", data: "API calls, platform interactions, feature usage, timestamps", purpose: "Service delivery, analytics, and optimization" },
  { cat: "Payment Information", data: "Billing address, payment method details (processed by Stripe)", purpose: "Payment processing and invoicing" },
  { cat: "Communication Data", data: "Emails, support tickets, contact form submissions", purpose: "Customer support and communication" },
  { cat: "Technical Data", data: "IP address, browser type, device information, cookies", purpose: "Security, fraud prevention, and website functionality" },
  { cat: "AI Workflow Data", data: "Prompts, model configurations, orchestration parameters", purpose: "Service delivery (processed per your instructions, not used for training)" },
];

function Section({ num, title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-base font-semibold text-[var(--qn-foreground)] mb-3">{num}. {title}</h2>
      <div className="text-sm text-[var(--qn-foreground-muted)] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-4xl sm:text-5xl font-bold mb-2" data-testid="privacy-heading">Privacy Policy</h1>
          <p className="text-sm text-[var(--qn-foreground-subtle)] mb-1">Personvernerkl&aelig;ring</p>
          <p className="text-sm text-[var(--qn-foreground-subtle)] mb-12">Effective Date: April 1, 2026</p>
        </motion.div>

        <Section num={1} title="Introduction">
          <p>Global InTech AS (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), organisasjonsnummer NO 935 706 998, is the data controller responsible for the processing of personal data collected through the QNNEAL platform and this website. We are committed to protecting your privacy and processing your personal data in accordance with the EU General Data Protection Regulation (GDPR) and the Norwegian Personal Data Act (Personopplysningsloven).</p>
          <p>This Privacy Policy explains what personal data we collect, why we collect it, how we use it, and your rights regarding your data.</p>
        </Section>

        <Section num={2} title="Data Controller">
          <div className="p-4 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]">
            <p className="font-medium text-[var(--qn-foreground)]">Global InTech AS</p>
            <p>R&oslash;o 95, 5457 H&oslash;ylandsbygd, Norway</p>
            <p>Email: privacy@qnneal.dev</p>
            <p>Phone: +47 404 00 755</p>
          </div>
        </Section>

        <Section num={3} title="Personal Data We Collect">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-[var(--qn-border)] rounded-lg overflow-hidden">
              <thead><tr className="bg-[var(--qn-surface)]"><th className="p-3 text-left font-medium text-[var(--qn-foreground)]">Category</th><th className="p-3 text-left font-medium text-[var(--qn-foreground)]">Data Collected</th><th className="p-3 text-left font-medium text-[var(--qn-foreground)]">Purpose</th></tr></thead>
              <tbody>{DATA_TABLE.map((r, i) => (<tr key={i} className="border-t border-[var(--qn-border)]"><td className="p-3 font-medium">{r.cat}</td><td className="p-3">{r.data}</td><td className="p-3">{r.purpose}</td></tr>))}</tbody>
            </table>
          </div>
        </Section>

        <Section num={4} title="Legal Basis for Processing">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Contract performance</strong> (Art. 6(1)(b)): Processing necessary to provide the QNNEAL platform services you have subscribed to.</li>
            <li><strong>Legitimate interest</strong> (Art. 6(1)(f)): Processing for analytics, security, fraud prevention, and service improvement, where our interests do not override your fundamental rights.</li>
            <li><strong>Consent</strong> (Art. 6(1)(a)): Processing for marketing communications and non-essential cookies, based on your explicit consent.</li>
            <li><strong>Legal obligation</strong> (Art. 6(1)(c)): Processing required to comply with Norwegian tax, accounting, and regulatory requirements.</li>
          </ul>
        </Section>

        <Section num={5} title="Data Sharing and Transfers">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>AI Providers:</strong> When you use the QNNEAL platform, your prompts and workflow data are routed to third-party AI providers (e.g., OpenAI, Anthropic, Google) as necessary to deliver the service.</li>
            <li><strong>Payment Processors:</strong> Stripe processes payment information on our behalf under their own data processing agreement.</li>
            <li><strong>Infrastructure Providers:</strong> Cloud hosting and infrastructure services necessary to operate the platform.</li>
            <li><strong>Legal Authorities:</strong> When required by Norwegian or EU law, court order, or regulatory request.</li>
          </ul>
          <p>For transfers of personal data outside the European Economic Area (EEA), we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) approved by the European Commission.</p>
        </Section>

        <Section num={6} title="Data Retention">
          <ul className="list-disc pl-5 space-y-2">
            <li>Account data: retained for the duration of your account plus 12 months after deletion.</li>
            <li>Payment records: retained for 5 years in accordance with Norwegian bookkeeping legislation (Bokf&oslash;ringsloven).</li>
            <li>Usage and analytics data: retained in anonymized form for up to 24 months.</li>
            <li>Communication records: retained for 24 months after the last interaction.</li>
          </ul>
        </Section>

        <Section num={7} title="Your Rights">
          <p>Under GDPR and Norwegian law, you have the following rights:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Right of access</strong> (Art. 15): Request a copy of the personal data we hold about you.</li>
            <li><strong>Right to rectification</strong> (Art. 16): Request correction of inaccurate or incomplete data.</li>
            <li><strong>Right to erasure</strong> (Art. 17): Request deletion of your personal data (&ldquo;right to be forgotten&rdquo;).</li>
            <li><strong>Right to restrict processing</strong> (Art. 18): Request limitation of how we process your data.</li>
            <li><strong>Right to data portability</strong> (Art. 20): Receive your data in a structured, machine-readable format.</li>
            <li><strong>Right to object</strong> (Art. 21): Object to processing based on legitimate interest or direct marketing.</li>
            <li><strong>Right to withdraw consent:</strong> Withdraw consent at any time without affecting the lawfulness of prior processing.</li>
          </ul>
          <p><strong>Self-Service Data Controls:</strong> QNNEAL provides built-in tools to exercise your data rights directly from your account:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Data Export:</strong> Request a complete export of your personal data, usage history, and API key metadata from Profile &rarr; Data &amp; Privacy &rarr; Request Data Export. Exports are delivered in machine-readable JSON format.</li>
            <li><strong>Account Deletion:</strong> Permanently delete your account and all associated data from Profile &rarr; Data &amp; Privacy &rarr; Delete Account. This action is irreversible and removes all workflows, API keys, optimization history, and audit logs.</li>
            <li><strong>Profile Management:</strong> Edit or remove personal information at any time from your Profile settings.</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href="mailto:privacy@qnneal.dev" className="text-[var(--qn-primary)] hover:underline">privacy@qnneal.dev</a> or use the self-service tools in your dashboard. We will respond within 30 days as required by GDPR.</p>
        </Section>

        <Section num={8} title="Cookies">
          <p>We use cookies and similar technologies to operate our website and platform. You can manage your cookie preferences at any time through the cookie settings banner on our website.</p>
        </Section>

        <Section num={9} title="Data Security">
          <p>We implement appropriate technical and organizational measures to protect your personal data, including:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
            <li>Multi-factor authentication (TOTP) for user accounts</li>
            <li>Role-based access control (Owner, Admin, Developer, Viewer) with per-user data isolation</li>
            <li>API keys stored as SHA-256 hashes (never in plaintext)</li>
            <li>Session management with device tracking, IP logging, and remote revocation</li>
            <li>Comprehensive audit logging of all security-relevant events, retained 90 days</li>
            <li>Rate limiting on authentication endpoints to prevent brute-force attacks</li>
            <li>Account lockout after 5 failed login attempts (30-minute cooldown)</li>
          </ul>
        </Section>

        <Section num={10} title="Supervisory Authority">
          <p>If you believe we have not handled your personal data in accordance with applicable law, you have the right to lodge a complaint with the Norwegian Data Protection Authority (Datatilsynet):</p>
          <div className="mt-2 p-4 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]">
            <p className="font-medium text-[var(--qn-foreground)]">Datatilsynet</p>
            <p>Postboks 458 Sentrum, 0105 Oslo, Norway</p>
            <p><a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" className="text-[var(--qn-primary)] hover:underline">www.datatilsynet.no</a></p>
          </div>
        </Section>

        <Section num={11} title="Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. Material changes will be communicated via email or a prominent notice on our website. The &ldquo;Effective Date&rdquo; at the top of this page indicates when the policy was last revised.</p>
        </Section>

        <Section num={12} title="Contact">
          <p>For privacy-related inquiries: <a href="mailto:privacy@qnneal.dev" className="text-[var(--qn-primary)] hover:underline">privacy@qnneal.dev</a></p>
        </Section>
      </div>
    </PageLayout>
  );
}
